// Live preview panel — right column on desktop.
import React, { useState, useEffect } from "react";
import { Check, AlertCircle } from "lucide-react";
import { SEV_META, CATEGORY_LABEL, CONFIDENCE_META } from "./aiChatConstants";

const FIELD_PROMPT = {
  title: "Which line is affected? Or describe what happened.",
  category: "What kind of issue is this?",
  severity: "Is the line stopped right now?",
  description: "Can you describe what happened in more detail?",
  affected_line: "Which line is affected?",
  estimated_units_impact: "How many units are affected?",
  estimated_hours_impact: "How long has the issue been ongoing?",
  estimated_eur_loss: "Estimated financial impact in EUR?",
  affected_part_numbers: "Do you have a part number?",
  suggested_parties: "Which supplier or party is involved?",
};

const SLA_INFO = {
  s1: { color: "#E24B4A", bg: "rgba(226,75,74,0.10)", text: "S1 SLA · Ack 15 min · Resolve 4h · Timer starts on create" },
  s2: { color: "#f3b14a", bg: "rgba(243,177,74,0.10)", text: "S2 SLA · Ack 1h · Resolve 8h" },
  s3: { color: "#185FA5", bg: "rgba(24,95,165,0.10)", text: "S3 SLA · Ack 4h · Resolve 24h" },
  s4: { color: "#9198b0", bg: "rgba(145,152,176,0.10)", text: "S4 SLA · Ack 4h · Resolve 48h" },
};

export const ChatIncidentPreview = ({ extracted, confidence, missing }) => {
  const sev = extracted.severity ? SEV_META[extracted.severity] : null;
  const conf = CONFIDENCE_META[confidence] || CONFIDENCE_META.low;
  const required = ["title", "category", "severity"];
  const allRequiredFilled = required.every((k) => extracted[k]);
  const isReady = allRequiredFilled && confidence === "high";
  const isFilled = (k) => Boolean(extracted[k]) || (Array.isArray(extracted[k]) && extracted[k].length > 0);
  const nextField = Array.isArray(missing) && missing.length ? missing[0] : null;
  const slaInfo = extracted.severity ? SLA_INFO[extracted.severity] : null;

  return (
    <div
      className="rounded-lg p-4 space-y-4 transition-all"
      style={{
        background: "#0d0f18",
        border: isReady ? "1px solid #4ed084" : "1px solid rgba(255,255,255,0.10)",
        boxShadow: isReady ? "0 0 0 1px rgba(78,208,132,0.25)" : "none",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div
          style={{
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 13,
            color: "#f0f2f8",
            letterSpacing: "0.4px",
          }}
        >
          Incident Preview
        </div>
        <span
          style={{
            background: conf.bg,
            border: `1px solid ${conf.color}55`,
            color: conf.color,
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            letterSpacing: "1.2px",
            padding: "2px 6px",
            borderRadius: 4,
          }}
          data-testid="ai-preview-confidence"
        >
          {conf.label}
        </span>
      </div>

      {/* "Next field" hint */}
      {nextField && !isReady && (
        <div
          className="rounded px-3 py-2"
          style={{
            background: "rgba(155,89,182,0.08)",
            border: "1px solid rgba(155,89,182,0.35)",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            color: "#c89adb",
            letterSpacing: "0.4px",
          }}
          data-testid="ai-preview-next-field"
          data-next-field={nextField}
        >
          → Next: {FIELD_PROMPT[nextField] || nextField}
        </div>
      )}

      {/* Severity badge */}
      <AnimatedField filled={isFilled("severity")} pulsing={nextField === "severity"}>
        {sev ? (
          <div
            className="rounded px-3 py-2"
            style={{
              background: sev.bg,
              border: `1px solid ${sev.color}55`,
              color: sev.color,
              fontFamily: "Syne, sans-serif",
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: "0.4px",
            }}
            data-testid="ai-preview-severity"
          >
            {sev.label}
          </div>
        ) : (
          <PreviewPlaceholder label="Severity" />
        )}
      </AnimatedField>

      {/* SLA info card — appears as soon as severity confirmed */}
      {slaInfo && (
        <div
          className="rounded px-3 py-2"
          style={{
            background: slaInfo.bg,
            border: `1px solid ${slaInfo.color}55`,
            color: slaInfo.color,
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.3px",
          }}
          data-testid="ai-preview-sla"
        >
          ⏱ {slaInfo.text}
        </div>
      )}

      {/* Category */}
      <AnimatedField filled={isFilled("category")} pulsing={nextField === "category"}>
        <PreviewField
          label="Category"
          value={extracted.category ? CATEGORY_LABEL[extracted.category] || extracted.category : null}
          testid="ai-preview-category"
        />
      </AnimatedField>

      {/* Title */}
      <AnimatedField filled={isFilled("title")} pulsing={nextField === "title"}>
        <PreviewField
          label="Title"
          value={extracted.title}
          testid="ai-preview-title"
          mono={false}
        />
      </AnimatedField>

      {/* Description */}
      <AnimatedField filled={isFilled("description")} pulsing={nextField === "description"}>
        <PreviewField
          label="Description"
          value={extracted.description}
          testid="ai-preview-description"
          clamp={3}
        />
      </AnimatedField>

      {/* Affected line */}
      <AnimatedField filled={isFilled("affected_line")} pulsing={nextField === "affected_line"}>
        <PreviewField
          label="Affected Line"
          value={extracted.affected_line}
          testid="ai-preview-line"
        />
      </AnimatedField>

      {/* Part numbers */}
      <AnimatedField filled={isFilled("affected_part_numbers")} pulsing={nextField === "affected_part_numbers"}>
        <PreviewTags
          label="Part Numbers"
          values={extracted.affected_part_numbers || []}
          color="#9ec3ec"
          testid="ai-preview-parts"
        />
      </AnimatedField>

      {/* Impact */}
      <AnimatedField
        filled={Boolean(extracted.estimated_units_impact || extracted.estimated_hours_impact || extracted.estimated_eur_loss)}
        pulsing={nextField === "estimated_units_impact" || nextField === "estimated_hours_impact" || nextField === "estimated_eur_loss"}
      >
        <PreviewField
          label="Estimated Impact"
          value={
            (extracted.estimated_units_impact || extracted.estimated_hours_impact || extracted.estimated_eur_loss)
              ? `${extracted.estimated_units_impact || 0} units · ${extracted.estimated_hours_impact || 0}h · €${extracted.estimated_eur_loss || 0}`
              : null
          }
          testid="ai-preview-impact"
        />
      </AnimatedField>

      {/* Parties */}
      <AnimatedField filled={isFilled("suggested_parties")} pulsing={nextField === "suggested_parties"}>
        <PreviewTags
          label="Suggested Parties"
          values={extracted.suggested_parties || []}
          color="#c89adb"
          testid="ai-preview-parties"
        />
      </AnimatedField>

      {/* Required fields checklist */}
      <div
        className="mt-3 pt-3 space-y-1.5"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="ai-preview-checklist"
      >
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            color: "#9198b0",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          Required fields
        </div>
        <ChecklistRow label="Title" filled={isFilled("title")} testid="ai-checklist-title" />
        <ChecklistRow label="Category" filled={isFilled("category")} testid="ai-checklist-category" />
        <ChecklistRow label="Severity" filled={isFilled("severity")} testid="ai-checklist-severity" />
        <ChecklistRow label="Description" filled={isFilled("description")} optional testid="ai-checklist-description" />
      </div>

      {isReady && (
        <div
          className="rounded px-3 py-2 text-center"
          style={{
            background: "rgba(78,208,132,0.10)",
            border: "1px solid rgba(78,208,132,0.45)",
            color: "#4ed084",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
          data-testid="ai-preview-ready"
        >
          ✓ Ready to create
        </div>
      )}
    </div>
  );
};

const AnimatedField = ({ filled, pulsing, children }) => {
  const [mounted, setMounted] = useState(filled);
  useEffect(() => {
    if (filled && !mounted) setMounted(true);
  }, [filled, mounted]);
  return (
    <div
      className={pulsing && !filled ? "ai-pulse" : ""}
      style={{
        opacity: filled ? 1 : 0.45,
        transform: filled && mounted ? "translateY(0)" : "translateY(-2px)",
        transition: "opacity 360ms ease, transform 360ms ease",
        borderRadius: 6,
      }}
    >
      {children}
      <style>{`
        @keyframes ai-pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(155,89,182,0); }
          50% { box-shadow: 0 0 0 3px rgba(155,89,182,0.30); }
        }
        .ai-pulse { animation: ai-pulse-glow 1.8s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

const PreviewField = ({ label, value, testid, clamp, mono = true }) => (
  <div data-testid={testid}>
    <div
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        color: "#9198b0",
        letterSpacing: "1.2px",
        textTransform: "uppercase",
        marginBottom: 3,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {label}
      {value && <Check size={10} style={{ color: "#4ed084" }} />}
    </div>
    <div
      style={{
        fontFamily: mono ? "'DM Mono', monospace" : "Syne, sans-serif",
        fontSize: mono ? 12 : 14,
        color: value ? "#f0f2f8" : "#5a6080",
        lineHeight: 1.4,
        overflow: clamp ? "hidden" : "visible",
        display: clamp ? "-webkit-box" : "block",
        WebkitLineClamp: clamp,
        WebkitBoxOrient: "vertical",
      }}
    >
      {value || "—"}
    </div>
  </div>
);

const PreviewPlaceholder = ({ label }) => (
  <div
    className="rounded px-3 py-2"
    style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px dashed rgba(255,255,255,0.10)",
      color: "#5a6080",
      fontFamily: "'DM Mono', monospace",
      fontSize: 11,
    }}
  >
    {label} not yet detected
  </div>
);

const PreviewTags = ({ label, values, color, testid }) => (
  <div data-testid={testid}>
    <div
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        color: "#9198b0",
        letterSpacing: "1.2px",
        textTransform: "uppercase",
        marginBottom: 4,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {label}
      {values?.length > 0 && <Check size={10} style={{ color: "#4ed084" }} />}
    </div>
    {values?.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {values.map((v, i) => (
          <span
            key={i}
            className="inline-block px-2 py-0.5 rounded"
            style={{
              background: `${color}15`,
              border: `1px solid ${color}45`,
              color,
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
            }}
          >
            {v}
          </span>
        ))}
      </div>
    ) : (
      <div style={{ color: "#5a6080", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>—</div>
    )}
  </div>
);

const ChecklistRow = ({ label, filled, optional, testid }) => (
  <div
    className="flex items-center gap-2"
    style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}
    data-testid={testid}
    data-filled={filled ? "1" : "0"}
  >
    <span
      className="inline-flex items-center justify-center rounded-sm"
      style={{
        width: 14,
        height: 14,
        background: filled ? "rgba(78,208,132,0.18)" : "transparent",
        border: filled ? "1px solid #4ed084" : "1px solid rgba(255,255,255,0.15)",
        color: filled ? "#4ed084" : "transparent",
      }}
    >
      {filled && <Check size={9} strokeWidth={3} />}
    </span>
    <span style={{ color: filled ? "#cfd1d8" : "#9198b0" }}>
      {label}
      {optional && (
        <span style={{ color: "#5a6080", marginLeft: 4, fontSize: 9 }}>(optional)</span>
      )}
    </span>
  </div>
);

